import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSip } from '@/contexts/SipContext';
import { Wifi, WifiOff, Phone, PhoneOff, Shield, ShieldOff, RefreshCw } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { API_BASE_URL } from '@/utils/api';
import axios from 'axios';

function parseJwtExp(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export default function ConnectionStatusIndicator() {
  const { token, user, logout } = useAuth();
  const { sipStatus } = useSip();
  const [apiStatus, setApiStatus] = useState('ok'); // 'ok' | 'expiring' | 'expired'
  const [showReauth, setShowReauth] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);
  const [reauthError, setReauthError] = useState('');
  const checkIntervalRef = useRef(null);

  // Check token health periodically
  const checkTokenHealth = useCallback(async () => {
    if (!token) {
      setApiStatus('expired');
      return;
    }

    const expMs = parseJwtExp(token);
    if (!expMs) {
      setApiStatus('ok');
      return;
    }

    const now = Date.now();
    const remaining = expMs - now;
    const oneHour = 60 * 60 * 1000;

    if (remaining <= 0) {
      setApiStatus('expired');
      return;
    }

    if (remaining <= oneHour) {
      setApiStatus('expiring');
      return;
    }

    // Also do a lightweight API ping to verify token is actually valid
    try {
      await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      setApiStatus('ok');
    } catch (err) {
      if (err.response?.status === 401) {
        setApiStatus('expired');
      }
    }
  }, [token]);

  useEffect(() => {
    checkTokenHealth();
    // Check every 5 minutes
    checkIntervalRef.current = setInterval(checkTokenHealth, 5 * 60 * 1000);
    return () => clearInterval(checkIntervalRef.current);
  }, [checkTokenHealth]);

  // Global 401 interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          setApiStatus('expired');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  const handleReauth = async (e) => {
    e.preventDefault();
    setReauthLoading(true);
    setReauthError('');
    try {
      const phone = user?.phone_number || localStorage.getItem('last_phone');
      if (!phone) {
        setReauthError('Unable to determine phone number. Please sign out and log in again.');
        setReauthLoading(false);
        return;
      }

      const res = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        phone_number: phone,
        password: reauthPassword,
      });

      const newToken = res.data.access_token;
      localStorage.setItem('token', newToken);
      // Force page reload to pick up new token across all contexts
      window.location.reload();
    } catch (err) {
      setReauthError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setReauthLoading(false);
    }
  };

  // Derive SIP statuses
  const sipConnected = sipStatus === 'registered';
  const sipConnecting = sipStatus === 'connecting';
  const wsConnected = ['registered', 'connecting'].includes(sipStatus);

  // Overall health
  const allGood = apiStatus === 'ok' && sipConnected;
  const hasWarning = apiStatus === 'expiring' || sipConnecting;
  const hasError = apiStatus === 'expired' || (!sipConnected && !sipConnecting);

  const indicators = [
    {
      key: 'api',
      label: 'API Session',
      ok: apiStatus === 'ok',
      warn: apiStatus === 'expiring',
      error: apiStatus === 'expired',
      detail: apiStatus === 'ok' ? 'Authenticated' : apiStatus === 'expiring' ? 'Token expiring soon' : 'Session expired',
      Icon: apiStatus === 'ok' ? Shield : ShieldOff,
    },
    {
      key: 'sip',
      label: 'SIP Registration',
      ok: sipConnected,
      warn: sipConnecting,
      error: !sipConnected && !sipConnecting,
      detail: sipConnected ? 'Registered' : sipConnecting ? 'Connecting...' : sipStatus === 'failed' ? 'Registration failed' : 'Disconnected',
      Icon: sipConnected ? Phone : PhoneOff,
    },
    {
      key: 'ws',
      label: 'WebSocket',
      ok: wsConnected,
      warn: false,
      error: !wsConnected,
      detail: wsConnected ? 'Connected' : 'Disconnected',
      Icon: wsConnected ? Wifi : WifiOff,
    },
  ];

  function dotColor(ind) {
    if (ind.ok) return 'bg-emerald-500';
    if (ind.warn) return 'bg-amber-500 animate-pulse';
    return 'bg-red-500';
  }

  return (
    <TooltipProvider>
      <div className="fixed top-3 right-4 z-50 flex items-center gap-2" data-testid="connection-status-indicator">
        {/* Compact pill showing overall status */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border backdrop-blur-sm transition-all duration-300 cursor-default ${
                allGood
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  : hasWarning
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                  : 'bg-red-500/10 border-red-500/30 text-red-400 cursor-pointer'
              }`}
              onClick={() => {
                if (apiStatus === 'expired') setShowReauth(true);
              }}
              data-testid="connection-status-pill"
            >
              {/* Three small dots for API / SIP / WS */}
              {indicators.map((ind) => (
                <span
                  key={ind.key}
                  className={`w-2 h-2 rounded-full ${dotColor(ind)}`}
                  data-testid={`status-dot-${ind.key}`}
                />
              ))}
              <span className="ml-1">
                {allGood ? 'Connected' : hasWarning ? 'Warning' : 'Disconnected'}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="w-64 p-0">
            <div className="p-3 space-y-2">
              <p className="text-xs font-semibold text-foreground mb-2">Connection Status</p>
              {indicators.map((ind) => (
                <div key={ind.key} className="flex items-center justify-between" data-testid={`status-row-${ind.key}`}>
                  <div className="flex items-center gap-2">
                    <ind.Icon className={`w-3.5 h-3.5 ${ind.ok ? 'text-emerald-400' : ind.warn ? 'text-amber-400' : 'text-red-400'}`} />
                    <span className="text-xs text-muted-foreground">{ind.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-medium ${ind.ok ? 'text-emerald-400' : ind.warn ? 'text-amber-400' : 'text-red-400'}`}>
                      {ind.detail}
                    </span>
                    <span className={`w-2 h-2 rounded-full ${dotColor(ind)}`} />
                  </div>
                </div>
              ))}
              {apiStatus === 'expired' && (
                <button
                  className="w-full mt-2 px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                  onClick={() => setShowReauth(true)}
                  data-testid="reauth-trigger-btn"
                >
                  Re-authenticate
                </button>
              )}
              {apiStatus === 'expiring' && (
                <p className="text-xs text-amber-400 mt-1">Your session will expire soon. Save your work.</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>

        {/* Re-authentication modal */}
        {showReauth && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="reauth-modal">
            <div className="bg-card border border-border rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
              <div className="flex items-center gap-2 mb-4">
                <ShieldOff className="w-5 h-5 text-red-400" />
                <h3 className="text-base font-semibold text-foreground">Session Expired</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Your API session has expired. Enter your password to continue without losing your current state.
              </p>
              <form onSubmit={handleReauth}>
                <input
                  type="password"
                  value={reauthPassword}
                  onChange={(e) => setReauthPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 mb-3"
                  autoFocus
                  data-testid="reauth-password-input"
                />
                {reauthError && (
                  <p className="text-xs text-red-400 mb-2" data-testid="reauth-error">{reauthError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={reauthLoading || !reauthPassword}
                    className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    data-testid="reauth-submit-btn"
                  >
                    {reauthLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    Re-authenticate
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowReauth(false); setReauthError(''); setReauthPassword(''); }}
                    className="px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="reauth-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
              <button
                onClick={() => { logout(); window.location.href = '/login'; }}
                className="w-full mt-3 text-xs text-muted-foreground hover:text-red-400 transition-colors"
                data-testid="reauth-logout-btn"
              >
                Or sign out completely
              </button>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
